import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { stripeService, BillingInfo } from "@/services/stripe";
import { BillingTransaction } from "../types/api";

import { TrendingUp, FileText, Plus, ExternalLink, Ticket } from "lucide-react";

const PRESET_AMOUNTS = [50, 250, 1000];

export function Billing() {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [customAmount, setCustomAmount] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const { toast } = useToast();

  // Check if user is Azure marketplace user
  const isAzureUser = billingInfo
    ? stripeService.isAzureMarketplaceUser(billingInfo)
    : false;

  useEffect(() => {
    loadBillingData();

    // Check for success/cancel parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("success") === "true") {
      toast({
        title: "Payment Successful",
        description: "Your credits have been added to your account.",
      });
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (urlParams.get("cancelled") === "true") {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [info, transactionHistory] = await Promise.all([
        stripeService.getBillingInfo(),
        stripeService.getTransactions(10, 0),
      ]);

      setBillingInfo(info);
      setTransactions(transactionHistory);
    } catch (error) {
      console.error("Error loading billing data:", error);
      toast({
        title: "Error",
        description: "Failed to load billing information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async (amount: number) => {
    try {
      setProcessingPayment(true);
      await stripeService.purchaseCredits(amount);
    } catch (error) {
      console.error("Error purchasing credits:", error);
      let errorMessage = "Failed to start checkout process";

      if (error instanceof Error) {
        if (error.message.includes("Stripe failed to initialize")) {
          errorMessage =
            "Stripe is not properly configured. Please check your environment variables.";
        } else if (
          error.message.includes("Failed to create checkout session")
        ) {
          errorMessage =
            "Unable to create payment session. Please try again or contact support.";
        } else if (
          error.message.includes("Network Error") ||
          error.message.includes("fetch")
        ) {
          errorMessage =
            "Network error. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCustomAmountPurchase = async () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount greater than 0",
        variant: "destructive",
      });
      return;
    }

    if (amount < 5) {
      toast({
        title: "Minimum Amount",
        description: "Minimum purchase amount is $5.00",
        variant: "destructive",
      });
      return;
    }

    await handlePurchaseCredits(amount);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case "Deposit":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Billing & Credits</h1>
      </div>

      {/* Azure Marketplace Info */}
      {isAzureUser && (
        <Card className="mb-8 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-blue-900">
                  Azure Marketplace Billing
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your billing is managed through Azure Marketplace. Usage
                  charges are automatically billed to your Azure subscription at
                  the end of each billing period. No prepaid credits are
                  required.
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  You can view detailed usage and billing information in the
                  Azure billing portal.
                </p>
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-700 border-blue-300 hover:bg-blue-100"
                    onClick={() => {
                      const portalUrl = billingInfo
                        ? stripeService.getAzureBillingPortalUrl(billingInfo)
                        : null;
                      if (portalUrl) {
                        window.open(portalUrl, "_blank", "noopener,noreferrer");
                      }
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Azure Billing Portal
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Current Credit Balance
            </CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.floor(billingInfo?.balance || 0)} credits
            </div>
            <p className="text-xs text-muted-foreground">Available credits</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pricing</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              <div>
                good-as-a-human: {billingInfo?.pricing.goodAsHumanCredits ?? 8}{" "}
                credits
              </div>
              <div>
                better-than-a-human:{" "}
                {billingInfo?.pricing.betterThanHumanCredits ?? 10} credits
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Per document</p>
          </CardContent>
        </Card>
      </div>

      {/* Purchase Credits */}
      {!isAzureUser && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Purchase Credits</CardTitle>
            <CardDescription>
              Add credits to your account to process documents. Credits are
              prepaid and deducted when documents are processed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Preset Amounts */}
              <div>
                <Label className="text-sm font-medium">Quick Purchase</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      variant="outline"
                      className="h-16 text-lg"
                      onClick={() => handlePurchaseCredits(amount)}
                      disabled={processingPayment}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-bold">
                          {formatCurrency(amount)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Grants ~{Math.floor(amount * 40)} credits
                        </span>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <Label htmlFor="custom-amount" className="text-sm font-medium">
                  Custom Amount (USD)
                </Label>
                <div className="flex space-x-2 mt-2">
                  <Input
                    id="custom-amount"
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    min="5"
                    step="0.01"
                    className="flex-1"
                  />
                  <Button
                    onClick={handleCustomAmountPurchase}
                    disabled={processingPayment || !customAmount}
                    className="px-6"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Purchase Credits
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum purchase: $5.00 (yields ~200 credits)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>
            Your recent billing transactions and credit usage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No transactions yet. Purchase credits to get started!
              </p>
            ) : (
              transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {transaction.transactionType === "Deposit" ? (
                        <Plus className="h-5 w-5 text-green-600" />
                      ) : (
                        <TrendingUp className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{transaction.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(transaction.createdAt)}
                      </p>
                      {transaction.metadata?.source === "azure" && (
                        <div className="mt-1 text-xs text-blue-700">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 border border-blue-200 mr-2">
                            Azure
                          </span>
                          {transaction.metadata.azureSubscriptionId && (
                            <span className="font-mono">
                              Sub:{" "}
                              {transaction.metadata.azureSubscriptionId.slice(
                                0,
                                8
                              )}
                              ...
                            </span>
                          )}
                          {transaction.metadata.azurePlanId && (
                            <span className="ml-2">
                              Plan: {transaction.metadata.azurePlanId}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge
                      className={getTransactionTypeColor(
                        transaction.transactionType
                      )}
                    >
                      {transaction.transactionType}
                    </Badge>
                    <div className="text-right">
                      <span
                        className={`font-medium ${
                          transaction.transactionType === "Deposit"
                            ? "text-green-600"
                            : "text-gray-600"
                        }`}
                      >
                        {transaction.amount} credits
                      </span>
                      {transaction.metadata?.source === "azure" && (
                        <p className="text-xs text-muted-foreground">
                          Billed to Azure
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
